import helper from 'store/helper';
import { PRIORITY_AXIS_MEDIA_SYNC } from 'domain/consts';
import { LTV_DETAIL_DEFAULT_DIMENSIONS } from 'views/pages/adebify/ltv-detail/components/dimensionsCatalog';
import { LTV_DETAIL_DEFAULT_METRICS } from 'views/pages/adebify/ltv-detail/components/metricsCatalog';
import types from './types';

const IDLE = 'idle';
const LOADING = 'loading';
const SUCCEEDED = 'succeeded';
const FAILED = 'failed';

// Default LTV evaluation period (overridden by account config when loaded)
const DEFAULT_LTV_EVALUATION_PERIOD = 90;

// Product axis constants
export const PRODUCT_AXIS_AD = 'ad_product';
export const PRODUCT_AXIS_PURCHASED = 'purchased_product';

// Default LTV display-items state per 要件.md req 5.5: 媒体項目で集計 +
// 広告対象商品軸.
const DEFAULT_DIMENSIONS = LTV_DETAIL_DEFAULT_DIMENSIONS;
const DEFAULT_METRICS = LTV_DETAIL_DEFAULT_METRICS;

export const initialState = {
  summary: {
    newCvTotal: 0,
    uu: 0,
    newAov: 0,
    ltv: 0,
    f2ConversionRate: 0,
    ltvRoas: 0,
  },
  table: {
    data: [],
    settings: {
      sort: {
        field: 'ltv',
        direction: 'desc',
      },
      pagination: {
        currentPage: 1,
        totalItems: 0,
        itemsPerPage: 50,
      },
    },
  },
  graphData: [],
  responseMeta: null,
  ltvEvaluationPeriod: DEFAULT_LTV_EVALUATION_PERIOD,
  axis: PRIORITY_AXIS_MEDIA_SYNC,
  productAxis: PRODUCT_AXIS_AD,
  // Dimension keys as an array (sent directly in POST body to jug-api).
  dimensions: DEFAULT_DIMENSIONS,
  // Metric keys as an array (sent directly in POST body to jug-api).
  metrics: DEFAULT_METRICS,
  // Filters mirrored from the global CommonBar filter store (already mapped
  // to the snake_case API keys expected by jug-api).
  filters: {},
  status: IDLE,
  error: null,
};

const requestStart = (state) => ({
  ...state,
  status: LOADING,
  error: null,
});

const fetchLtvDetailSucceeded = (state, action) => {
  const { summary, tableData, graphData, meta } = action.payload;
  return {
    ...state,
    summary: summary || state.summary,
    table: {
      ...state.table,
      data: tableData || [],
      settings: {
        ...state.table.settings,
        pagination: {
          ...state.table.settings.pagination,
          totalItems: meta?.total_count || 0,
        },
      },
    },
    graphData: graphData || [],
    responseMeta: meta || null,
    status: SUCCEEDED,
    error: null,
  };
};

const fetchLtvDetailFailed = (state, action) => {
  const { error } = action.payload;
  return {
    ...state,
    status: FAILED,
    error,
  };
};

const setLtvEvaluationPeriod = (state, action) => {
  const { period } = action.payload;
  return {
    ...state,
    ltvEvaluationPeriod: period,
  };
};

const updateSort = (state, action) => {
  const { field, direction } = action.payload;
  return {
    ...state,
    table: {
      ...state.table,
      settings: {
        ...state.table.settings,
        sort: { field, direction },
        pagination: {
          ...state.table.settings.pagination,
          currentPage: 1,
        },
      },
    },
  };
};

const updatePage = (state, action) => {
  const { page } = action.payload;
  return {
    ...state,
    table: {
      ...state.table,
      settings: {
        ...state.table.settings,
        pagination: {
          ...state.table.settings.pagination,
          currentPage: page,
        },
      },
    },
  };
};

// Resets pagination to page 1 — used by every dimension/metric mutation.
const resetPagination = (state) => ({
  ...state,
  table: {
    ...state.table,
    settings: {
      ...state.table.settings,
      pagination: {
        ...state.table.settings.pagination,
        currentPage: 1,
      },
    },
  },
});

const setAxis = (state, action) => ({
  ...resetPagination(state),
  axis: action.payload.axis,
});

const setProductAxis = (state, action) => ({
  ...resetPagination(state),
  productAxis: action.payload.productAxis,
});

const setDimensions = (state, action) => ({
  ...resetPagination(state),
  dimensions: action.payload.dimensions,
});

const setMetrics = (state, action) => ({
  ...resetPagination(state),
  metrics: action.payload.metrics,
});

const setFilters = (state, action) => ({
  ...resetPagination(state),
  filters: action.payload.filters,
});

// Atomic update from the 項目切替 modal — committed in one shot so the saga
// only fetches once.
const setDisplayItems = (state, action) => {
  const { axis, productAxis, dimensions, metrics } = action.payload;
  return {
    ...resetPagination(state),
    ...(axis !== undefined ? { axis } : {}),
    ...(productAxis !== undefined ? { productAxis } : {}),
    ...(dimensions !== undefined ? { dimensions } : {}),
    ...(metrics !== undefined ? { metrics } : {}),
  };
};

const AdebifyLtvDetailReducer = helper.createReducer(initialState, {
  [types.REQUEST_START]: requestStart,
  [types.FETCH_LTV_DETAIL_SUCCEEDED]: fetchLtvDetailSucceeded,
  [types.FETCH_LTV_DETAIL_FAILED]: fetchLtvDetailFailed,
  [types.SET_LTV_EVALUATION_PERIOD]: setLtvEvaluationPeriod,
  [types.UPDATE_SORT]: updateSort,
  [types.UPDATE_PAGE]: updatePage,
  [types.SET_AXIS]: setAxis,
  [types.SET_PRODUCT_AXIS]: setProductAxis,
  [types.SET_DIMENSIONS]: setDimensions,
  [types.SET_METRICS]: setMetrics,
  [types.SET_FILTERS]: setFilters,
  [types.SET_DISPLAY_ITEMS]: setDisplayItems,
  [types.HYDRATE_DISPLAY_ITEMS]: setDisplayItems,
});

export default AdebifyLtvDetailReducer;
